import type { DateRangeItem, DecadeRangeItem } from 'src/models/calendar';
import { dtFormat } from 'src/lib/dateHelper';

const getDayCount = (year: number, month: number): number => {
  const dict = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month === 1) {
    if ((year % 400 === 0) || (year % 4 === 0 && year % 100 !== 0)) return 29;
  }
  return dict[month];
};
const siblingsMonth = (v: Date, n: number): Date => {
  return new Date(v.getFullYear(), v.getMonth() + n);
};
const getYearMonth = (year: number, month: number): {
  year: number;
  month: number;
} => {
  if (month > 11) {
    year++;
    month = 0;
  } else if (month < 0) {
    year--;
    month = 11;
  }
  return {year: year, month: month};
};
const parse = (str: string, failureValue: Date | null = new Date()): Date | null => {
  if (!str) { return failureValue; }
  let ret: Date;
  const retFunc = () => {
    return isNaN(ret.getFullYear()) ? failureValue : ret;
  };
  let m: RegExpMatchArray | null;
  m = str.match(/^(\d{4})[^\d](\d{2})[^\d](\d{2})$/);
  if (m) {
    // yyyy/mm/dd的な
    ret = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
    return retFunc();
  }
  m = str.match(/^(\d{2})[^\d](\d{2})[^\d](\d{4})$/);
  if (m) {
    // dd/mm/yyyy的な
    ret = new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
    return retFunc();
  }
  // 他の入力フォーマットも対応したかったらここに追加する

  return failureValue;
};
const detectDateEmitFormat = (str: string): string => {
  let m: RegExpMatchArray | null;
  m = str.match(/^(\d{4})[^\d](\d{2})[^\d](\d{2})$/);
  if (m) return 'yyyy/mm/dd';
  m = str.match(/^(\d{2})[^\d](\d{2})[^\d](\d{4})$/);
  if (m) return 'mm/dd/yyyy';
  m = str.match(/^([^\d]{2})(\d+)年(\d+)月(\d+)日$/);
  if (m) return 'g年m月d日';
  return 'yyyy-mm-dd';
};

// 指定した currDate と表示ペイン数から、各ペインの日グリッド(6週×7日=42セル)と
// 年代選択用の10年レンジを組み立てて返す純関数。state を直接書き換えないため副作用を持たない。
const buildDateRanges = (
  currDate: Date,
  pane: number,
  disabledDaysOfWeek: string[],
  inputValue: string,
): { dateRange: DateRangeItem[][]; decadeRange: DecadeRangeItem[][] } => {
  const dateRange: DateRangeItem[][] = [];
  const decadeRange: DecadeRangeItem[][] = [];
  for (let p = 0; p < pane; p++) {
    const currMonth = siblingsMonth(currDate, p);
    const time = {
      year: currMonth.getFullYear(),
      month: currMonth.getMonth(),
    };
    const yearStr = time.year.toString();
    decadeRange[p] = [];
    const firstYearOfDecade = parseInt(yearStr.substring(0, yearStr.length - 1)) + 0 - 1;
    for (let i = 0; i < 12; i++) {
      decadeRange[p].push({ text: firstYearOfDecade + i + p * 10 });
    }
    dateRange[p] = [];
    const currMonthFirstDay = new Date(time.year, time.month, 1);
    let firstDayWeek = currMonthFirstDay.getDay() + 1;
    if (firstDayWeek === 0) firstDayWeek = 7;
    const dayCount = getDayCount(time.year, time.month);
    if (firstDayWeek > 1) {
      const preMonth = getYearMonth(time.year, time.month - 1);
      const prevMonthDayCount = getDayCount(preMonth.year, preMonth.month);
      for (let i = 1; i < firstDayWeek; i++) {
        const dayText = prevMonthDayCount - firstDayWeek + i + 1;
        dateRange[p].push({
          text: dayText,
          date: new Date(preMonth.year, preMonth.month, dayText),
          sclass: 'datepicker-item-gray',
        });
      }
    }
    for (let i = 1; i <= dayCount; i++) {
      const date = new Date(time.year, time.month, i);
      const week = date.getDay();
      let sclass = '';
      disabledDaysOfWeek.forEach((el) => {
        if (week === parseInt(el, 10)) sclass = 'datepicker-item-disable';
      });
      if (i === currDate.getDate()) {
        if (inputValue) {
          const valueDate = parse(inputValue);
          if (valueDate) {
            if (valueDate.getFullYear() === time.year && valueDate.getMonth() === time.month) {
              sclass = 'datepicker-dateRange-item-active';
            }
          }
        }
      }
      dateRange[p].push({ text: i, date, sclass });
    }
    if (dateRange[p].length < 42) {
      const nextMonthNeed = 42 - dateRange[p].length;
      const nextMonth = getYearMonth(time.year, time.month + 1);
      for (let i = 1; i <= nextMonthNeed; i++) {
        dateRange[p].push({
          text: i,
          date: new Date(nextMonth.year, nextMonth.month, i),
          sclass: 'datepicker-item-gray',
        });
      }
    }
  }
  return { dateRange, decadeRange };
};

const stringifyDecadeHeader = (date: Date, pan: number): string => {
  const yearStr = date.getFullYear().toString();
  const firstYearOfDecade = parseInt(yearStr.substring(0, yearStr.length - 1) + 0, 10) + (pan * 10);
  const lastYearOfDecade = firstYearOfDecade + 10;
  return firstYearOfDecade + '-' + lastYearOfDecade;
};

const stringifyDayHeader = (date: Date, month: number, format: string): string => {
  const d = siblingsMonth(date, month);
  return dtFormat(d, format);
};

const getYearHeaderValue = (date: Date, year: number): number => date.getFullYear() + year;

export {
  getDayCount,
  siblingsMonth,
  getYearMonth,
  parse,
  detectDateEmitFormat,
  buildDateRanges,
  stringifyDecadeHeader,
  stringifyDayHeader,
  getYearHeaderValue,
};
